import { useMemo, useState } from 'react';
import { ArrowRight, AtSign, CalendarDays, Check, ChevronDown, ChevronUp, Milestone as MilestoneIcon } from 'lucide-react';
import { useT } from '../../i18n';
import { useRole } from '../../context/RoleContext';
import { api } from '../../api/client';
import type { GridColumn, HomeData, HomeProject, HomeTask } from '../../types';
import { getClosedColumnIds, getDoneStatusIds } from '../../utils/stages';
import { projectDisplayCode } from '../../utils/identifiers';
import { taskSaveErrorMessage } from '../TaskMilestoneNotice';
import { isOverdue, meetingLabel, statusClass, whenLabel } from './homeFormat';
import { MiniTaskCard } from './MiniTaskCard';
import { taskStatusName } from '../../utils/taskStatusName';
import { useStageName } from '../ProjectStageBar';

// "Mes tâches": my open tasks, one column per project like on the boards, each
// task a card. Every column head has the same shape (project, then the milestone
// it is in and how far it is), so the cards line up across columns; a mention or
// the next meeting sits at the column's foot. A long column opens in place. On
// my own tasks (I'm the responsible) the status changes in place. Never sorted
// by date: dates live in Mon calendrier.

const SHOWN_PROJECTS = 6;
const SHOWN_TASKS = 4;

/** The milestone under way, else the next one — with its number in the plan. */
function currentMilestone(p: HomeProject) {
  const i = p.milestones.findIndex(m => m.status === 'active');
  const j = i >= 0 ? i : p.milestones.findIndex(m => m.status === 'pending');
  return j >= 0 ? { m: p.milestones[j], n: j + 1 } : null;
}

const byUrgency = (a: HomeTask, b: HomeTask) =>
  (isOverdue(a.due_date) ? 0 : 1) - (isOverdue(b.due_date) ? 0 : 1)
  || (a.due_date ?? '9999').localeCompare(b.due_date ?? '9999')
  || a.title.localeCompare(b.title);

export function MyTasks({ data, columns, onChanged }: { data: HomeData; columns: GridColumn[]; onChanged: () => void }) {
  const { t } = useT();
  const { canSeeTasks } = useRole();
  const [showAll, setShowAll] = useState(false);
  const [busy, setBusy] = useState<number | null>(null);
  const [expanded, setExpanded] = useState<ReadonlySet<number>>(new Set());
  const stageName = useStageName();

  const doneColumnId = useMemo(() => [...getDoneStatusIds(columns)][0], [columns]);
  // What the status picker offers: every column a task stays open in.
  const openColumns = useMemo(() => {
    const closed = getClosedColumnIds(columns);
    return [...columns].filter(c => !closed.has(c.id)).sort((a, b) => a.sort_order - b.sort_order);
  }, [columns]);

  const groups = useMemo(() => {
    const projects = new Map(data.projects.map(p => [p.id, p]));
    const byProject = new Map<number, HomeTask[]>();
    for (const task of data.tasks) byProject.set(task.project_id, [...(byProject.get(task.project_id) ?? []), task]);
    return [...byProject.entries()]
      .map(([id, tasks]) => ({ project: projects.get(id)!, tasks: [...tasks].sort(byUrgency) }))
      .filter(g => g.project)
      .sort((a, b) => byUrgency(a.tasks[0], b.tasks[0]));
  }, [data]);

  if (groups.length === 0) return null;
  const late = data.tasks.filter(x => isOverdue(x.due_date)).length;
  const shown = showAll ? groups : groups.slice(0, SHOWN_PROJECTS);

  const move = (task: HomeTask, columnId: number) => {
    if (!columnId || columnId === task.column_id) return;
    setBusy(task.id);
    api.moveTask(task.id, columnId).then(onChanged)
      .catch((e: Error & { code?: string }) => alert(taskSaveErrorMessage(e, t)))
      .finally(() => setBusy(null));
  };

  const statusControl = (task: HomeTask) => {
    const cls = `home-status ${statusClass(task.column_label)}`;
    const canMove = task.is_assignee && openColumns.some(c => c.id === task.column_id);
    if (!canMove) {
      return <span className={cls} title={task.is_assignee ? undefined : t('home.tasks.notResponsible')}><i aria-hidden="true" />{taskStatusName(task.column_label, t)}</span>;
    }
    return (
      <>
        <label className={`${cls} is-editable`}>
          <i aria-hidden="true" />
          <select value={task.column_id} disabled={busy === task.id}
            aria-label={t('home.tasks.status', { title: task.title })}
            onChange={(e) => move(task, Number(e.target.value))}>
            {openColumns.map(c => <option key={c.id} value={c.id}>{taskStatusName(c.label, t)}</option>)}
          </select>
          <ChevronDown size={11} aria-hidden="true" />
        </label>
        <span className="home-spacer" />
        {doneColumnId && (
          <button type="button" className="btn btn-sm home-done-btn" disabled={busy === task.id}
            title={t('home.tasks.markDone')} onClick={() => move(task, doneColumnId)}>
            <Check size={12} /> {t('home.tasks.done')}
          </button>
        )}
      </>
    );
  };

  return (
    <section className="home-section" aria-labelledby="home-tasks-title">
      <div className="home-section-head">
        <h2 className="section-label" id="home-tasks-title">{t('home.tasks.title')}</h2>
        <span className="home-section-summary">
          {t(data.tasks.length > 1 ? 'home.tasks.countMany' : 'home.tasks.countOne', { n: data.tasks.length, projects: groups.length })}
          {late > 0 && <> · <span className="home-late">{t('home.tasks.late', { n: late })}</span></>}
        </span>
        <span className="home-spacer" />
        {canSeeTasks && <a className="home-more-link" href="#tasks-calendar">{t('home.tasks.calendar')} <ArrowRight size={12} /></a>}
      </div>

      <div className="home-task-board">
        {shown.map(({ project, tasks }) => {
          const cur = currentMilestone(project);
          const more = tasks.length - SHOWN_TASKS;
          const open = expanded.has(project.id);
          const toggle = () => setExpanded(prev => {
            const next = new Set(prev);
            if (next.has(project.id)) next.delete(project.id); else next.add(project.id);
            return next;
          });
          const msNumber = new Map(project.milestones.map((m, i) => [m.id, { n: i + 1, name: m.name }]));
          return (
            <article key={project.id} className="home-tcol">
              <header className="home-tcol-head">
                <div className="home-tcol-title-row">
                  <span className="home-code">{projectDisplayCode(project)}</span>
                  <a className="home-tcol-title" href={`#project?id=${project.id}`} title={project.name}>{project.name}</a>
                  <span className="home-count" title={t(tasks.length > 1 ? 'home.tasks.countMany' : 'home.tasks.countOne', { n: tasks.length, projects: 1 })}>{tasks.length}</span>
                </div>
                <div className="home-tcol-ms">
                  {cur ? (
                    <>
                      <span className="home-tcol-ms-row">
                        <MilestoneIcon size={11} aria-hidden="true" />
                        <span className="home-tcol-ms-name" title={cur.m.name || undefined}>{t('home.spine.barLabel', { n: cur.n, name: cur.m.name || t('milestones.untitled') })}</span>
                        {cur.m.total > 0 && <span className="home-mono">{cur.m.done}/{cur.m.total}</span>}
                      </span>
                      <span className="home-progress-track home-progress-track--thin">
                        <span className="home-progress-fill" style={{ width: cur.m.total ? `${(cur.m.done / cur.m.total) * 100}%` : 0 }} />
                      </span>
                    </>
                  ) : (
                    <span className="home-tcol-ms-row home-meta">{stageName(project.status_label || '')}</span>
                  )}
                </div>
              </header>

              <div className="home-tcol-body">
                {(open ? tasks : tasks.slice(0, SHOWN_TASKS)).map(task => {
                  // The column already names the current milestone: a card repeats it only when it differs.
                  const ms = task.milestone_id && task.milestone_id !== cur?.m.id ? msNumber.get(task.milestone_id) : undefined;
                  return (
                    <MiniTaskCard
                      key={task.id}
                      task={task}
                      projectId={project.id}
                      milestone={ms ? (ms.name?.trim() ? t('tasks.cardMilestone', { n: ms.n, name: ms.name.trim() }) : t('tasks.cardMilestoneUntitled', { n: ms.n })) : null}
                      people={task.with.length ? t('home.tasks.with', { names: task.with.join(', ') }) : null}
                      footer={statusControl(task)}
                    />
                  );
                })}
                {more > 0 && (
                  <button type="button" className="home-tcol-more" aria-expanded={open} onClick={toggle}>
                    {open
                      ? <><ChevronUp size={12} aria-hidden="true" /> {t('home.tasks.fewer')}</>
                      : <><ChevronDown size={12} aria-hidden="true" /> {t(more > 1 ? 'home.tasks.moreMany' : 'home.tasks.moreOne', { n: more })}</>}
                  </button>
                )}
              </div>

              {(project.mention || project.next_meeting) && (
                <footer className="home-tcol-foot">
                  {project.mention && (
                    <div className="home-tcol-note"><AtSign size={12} aria-hidden="true" />
                      <span>{t('home.tasks.mention', { name: project.mention.by, when: whenLabel(project.mention.at, t) })}</span>
                    </div>
                  )}
                  {project.next_meeting && (
                    <div className="home-tcol-note"><CalendarDays size={12} aria-hidden="true" />
                      <span>{t('home.tasks.meeting', { title: project.next_meeting.subject || t('home.meetings.untitled'), when: meetingLabel(project.next_meeting.start_time, false, t) })}</span>
                    </div>
                  )}
                </footer>
              )}
            </article>
          );
        })}
      </div>

      {groups.length > SHOWN_PROJECTS && (
        <button type="button" className="btn btn-ghost btn-sm home-show-all" onClick={() => setShowAll(v => !v)}>
          {showAll ? t('home.tasks.showFewer') : t('home.tasks.showAll', { n: groups.length - SHOWN_PROJECTS })}
        </button>
      )}
    </section>
  );
}
